import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "@/components/ScrollToTop";
import { LanguageProvider } from "@/contexts/LanguageContext";
import Index from "./pages/Index";

// Lazy load route components for code splitting
const Flaecheneigentuemer = lazy(() => import("./pages/Flaecheneigentuemer"));
const Netzbetreiber = lazy(() => import("./pages/Netzbetreiber"));
const Kommunen = lazy(() => import("./pages/Kommunen"));
const UeberUns = lazy(() => import("./pages/UeberUns"));
const Kontakt = lazy(() => import("./pages/Kontakt"));
const Projekte = lazy(() => import("./pages/Projekte"));
const GridStable = lazy(() => import("./pages/GridStable"));
const Presse = lazy(() => import("./pages/Presse"));
const PresseArtikel = lazy(() => import("./pages/PresseArtikel"));
const Impressum = lazy(() => import("./pages/Impressum"));
const Datenschutz = lazy(() => import("./pages/Datenschutz"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ReachyKI = lazy(() => import("./pages/ReachyKI"));
const Projektmanagement = lazy(() => import("./pages/Projektmanagement"));
const UseCases = lazy(() => import("./pages/UseCases"));
const KIStartseite = lazy(() => import("./pages/KIStartseite"));
const KIPlayground = lazy(() => import("./pages/KIPlayground"));
const PlaygroundService = lazy(() => import("./pages/PlaygroundService"));
const PlaygroundAgent = lazy(() => import("./pages/PlaygroundAgent"));
const PlattformHub = lazy(() => import("./pages/plattform/Hub"));
const PlattformAnwendungen = lazy(() => import("./pages/plattform/Anwendungen"));
const PlattformWirtschaftlichkeit = lazy(() => import("./pages/plattform/Wirtschaftlichkeit"));
const PlattformReferenzen = lazy(() => import("./pages/plattform/Referenzen"));
const Neu = lazy(() => import("./pages/Neu"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LanguageProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
        <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/plattform" element={<PlattformHub />} />
            <Route path="/plattform/anwendungen" element={<PlattformAnwendungen />} />
            <Route path="/plattform/wirtschaftlichkeit" element={<PlattformWirtschaftlichkeit />} />
            <Route path="/plattform/referenzen" element={<PlattformReferenzen />} />
            <Route path="/flaecheneigentuemer" element={<Flaecheneigentuemer />} />
            <Route path="/netzbetreiber" element={<Netzbetreiber />} />
            <Route path="/kommunen" element={<Kommunen />} />
            <Route path="/gridstable" element={<GridStable />} />
            <Route path="/unternehmen/ueber-uns" element={<UeberUns />} />
            <Route path="/unternehmen/projekte" element={<Projekte />} />
            <Route path="/kontakt" element={<Kontakt />} />
            <Route path="/presse" element={<Presse />} />
            <Route path="/presse/:articleId" element={<PresseArtikel />} />
            
            {/* KI routes */}
            <Route path="/ki" element={<KIStartseite />} />
            <Route path="/ki/reachy" element={<ReachyKI />} />
            <Route path="/ki/asset-management" element={<Navigate to="/plattform" replace />} />
            <Route path="/ki/projektmanagement" element={<Projektmanagement />} />
            <Route path="/ki/use-cases" element={<UseCases />} />
            <Route path="/ki/playground" element={<KIPlayground />} />
            <Route path="/ki/playground/agent" element={<PlaygroundAgent />} />
            <Route path="/ki/playground/:serviceName" element={<PlaygroundService />} />

            {/* Wissen routes */}
            <Route path="/wissen/whitepaper" element={<Navigate to="/" replace />} />
            
            {/* Legal pages */}
            <Route path="/impressum" element={<Impressum />} />
            <Route path="/datenschutz" element={<Datenschutz />} />

            {/* Legacy aliases — real client-side redirects to canonical paths */}
            <Route path="/unternehmen/team" element={<Navigate to="/unternehmen/ueber-uns" replace />} />
            <Route path="/unternehmen/news" element={<Navigate to="/presse" replace />} />

            {/* Cinematic Scroll-Scrub Startseite */}
            <Route path="/neu" element={<Neu />} />

            {/* Catch-all route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
    </LanguageProvider>
  </QueryClientProvider>
);

export default App;
